import debounce from 'debounce';
import { useMemo, useRef } from 'react';

/**
 * Creates a debounced version of a callback that always calls the latest version.
 *
 * This solves the "stale closure" problem with debounced callbacks in React.
 * When using `useCallback` with an empty dependency array, the callback captures
 * values from the first render. This hook ensures the debounced function always
 * calls the latest version of the callback, with access to current props/state.
 *
 * @param callback - The function to debounce (can change between renders)
 * @param delay - Debounce delay in milliseconds
 * @returns A stable debounced function that calls the latest callback
 *
 * @example
 * const debouncedSave = useDebouncedCallback(
 *   async () => {
 *     await saveData(currentValue); // always uses latest currentValue
 *   },
 *   2000
 * );
 */
export function useDebouncedCallback<T extends (...args: never[]) => unknown>(
  callback: T,
  delay: number,
): (...args: Parameters<T>) => void {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  return useMemo(() => debounce((...args: Parameters<T>) => callbackRef.current(...args), delay), [delay]);
}
